<template>
  <div class="action-card" :class="`action-card--${stateClass}`">
    <div class="action-card__icon">
      <BaseIcon :name="iconName" size="14" />
    </div>

    <div class="action-card__body">
      <div class="action-card__header">
        <span class="action-card__label">{{ $t(toolLabelKey) }}</span>
        <span v-if="action.status === 'pending'" class="action-card__expires" :title="expiresFull">
          {{ expiresLabel }}
        </span>
      </div>

      <p class="action-card__summary">{{ action.summary }}</p>

      <ul v-if="previewEntries.length" class="action-card__preview">
        <li v-for="entry in previewEntries" :key="entry.key">
          <span class="action-card__preview-key">{{ entry.key }}:</span>
          <span class="action-card__preview-value">{{ entry.value }}</span>
        </li>
      </ul>

      <!-- Result block (after confirmation succeeds) -->
      <div v-if="action.status === 'confirmed'" class="action-card__result">
        <BaseIcon name="check-circle" size="12" />
        {{ $t('home.chat.action.confirmed') }}
      </div>

      <div v-if="action.status === 'expired'" class="action-card__result action-card__result--warning">
        <BaseIcon name="exclamation-triangle" size="12" />
        {{ $t('home.chat.action.expired') }}
      </div>

      <div v-if="action.status === 'dismissed'" class="action-card__result action-card__result--muted">
        <BaseIcon name="xmark" size="12" />
        {{ $t('home.chat.action.dismissed') }}
      </div>

      <div v-if="action.status === 'failed'" class="action-card__result action-card__result--error">
        <BaseIcon name="exclamation-circle" size="12" />
        {{ action.error || $t('home.chat.action.failed') }}
      </div>

      <div v-if="action.status === 'pending' || action.status === 'confirming'" class="action-card__actions">
        <Btn
          variant="primary"
          size="sm"
          icon="check"
          :loading="action.status === 'confirming'"
          @click="$emit('confirm', action.id)"
        >
          {{ $t('home.chat.action.confirm') }}
        </Btn>
        <Btn
          variant="tertiary"
          size="sm"
          :disabled="action.status === 'confirming'"
          @click="$emit('dismiss', action.id)"
        >
          {{ $t('home.chat.action.dismiss') }}
        </Btn>
      </div>
    </div>
  </div>
</template>

<script>
  import { computed } from 'vue';
  import dayjs from 'dayjs';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';

  // Display config per supported tool. Unknown tools fall back to a neutral
  // "voorgestelde actie" label.
  const TOOL_CONFIG = {
    propose_create_task: { icon: 'check-square', label_key: 'home.chat.action.tools.create_task' },
    propose_complete_task: { icon: 'check', label_key: 'home.chat.action.tools.complete_task' },
    propose_move_deal: { icon: 'handshake', label_key: 'home.chat.action.tools.move_deal' },
    propose_send_invoice: { icon: 'paper-plane', label_key: 'home.chat.action.tools.send_invoice' },
  };

  const FALLBACK_CONFIG = { icon: 'zap', label_key: 'home.chat.action.tools.generic' };

  // Friendlier preview-key labels (snake_case → human)
  const PREVIEW_KEY_LABELS = {
    title: 'Titel',
    description: 'Omschrijving',
    due_date: 'Vervaldatum',
    due_time: 'Tijd',
    priority: 'Prioriteit',
    assignee: 'Toegewezen aan',
    lead: 'Klant',
    deal: 'Deal',
    stage: 'Naar stage',
    invoice: 'Factuur',
    recipient: 'Naar',
    amount: 'Bedrag',
  };

  function formatPreviewValue(value) {
    if (value == null) return '—';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nee';
    if (Array.isArray(value)) return value.map(formatPreviewValue).join(', ');
    if (typeof value === 'object') {
      // Common shape: { id, name } or { id, title }
      return value.name || value.title || value.label || JSON.stringify(value);
    }
    return String(value);
  }

  export default {
    name: 'HomeChatActionCard',

    components: { BaseIcon, Btn },

    props: {
      action: { type: Object, required: true },
    },

    emits: ['confirm', 'dismiss'],

    setup(props) {
      const config = computed(() => TOOL_CONFIG[props.action.tool] || FALLBACK_CONFIG);

      const iconName = computed(() => config.value.icon);
      const toolLabelKey = computed(() => config.value.label_key);

      const previewEntries = computed(() => {
        if (!props.action.preview || typeof props.action.preview !== 'object') return [];
        return Object.entries(props.action.preview)
          .filter(([, v]) => v != null && v !== '')
          .map(([key, value]) => ({
            key: PREVIEW_KEY_LABELS[key] || key,
            value: formatPreviewValue(value),
          }));
      });

      const stateClass = computed(() => props.action.status || 'pending');

      const expiresFull = computed(() => {
        if (!props.action.expires_at) return '';
        return dayjs(props.action.expires_at).format('DD MMM HH:mm');
      });

      const expiresLabel = computed(() => {
        if (!props.action.expires_at) return '';
        const diff = dayjs(props.action.expires_at).diff(dayjs(), 'minute');
        if (diff <= 0) return 'verlopen';
        if (diff < 60) return `verloopt over ${diff} min`;
        const hours = Math.floor(diff / 60);
        return `verloopt over ${hours} uur`;
      });

      return {
        iconName,
        toolLabelKey,
        previewEntries,
        stateClass,
        expiresFull,
        expiresLabel,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .action-card {
    display: flex;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    margin-top: 0.625rem;
    border-radius: 0.625rem;
    border: 1px solid var(--amber-200);
    background: linear-gradient(180deg, var(--amber-50), #ffffff 70%);
    transition: all 0.2s ease;

    &--confirmed {
      border-color: var(--green-200);
      background: linear-gradient(180deg, var(--green-50), #ffffff 70%);
    }

    &--expired,
    &--dismissed {
      border-color: var(--slate-200);
      background: var(--slate-50);
      opacity: 0.85;
    }

    &--failed {
      border-color: var(--red-200);
      background: linear-gradient(180deg, var(--red-50), #ffffff 70%);
    }

    &__icon {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      border-radius: 0.5rem;
      background: var(--amber-100);
      color: var(--amber-700);
      display: inline-flex;
      align-items: center;
      justify-content: center;

      .action-card--confirmed & {
        background: var(--green-100);
        color: var(--green-700);
      }
      .action-card--expired &,
      .action-card--dismissed & {
        background: var(--slate-200);
        color: var(--slate-500);
      }
      .action-card--failed & {
        background: var(--red-100);
        color: var(--red-700);
      }
    }

    &__body {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
    }

    &__label {
      font-size: 0.6875rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--amber-800);

      .action-card--confirmed & {
        color: var(--green-800);
      }
      .action-card--expired &,
      .action-card--dismissed & {
        color: var(--slate-600);
      }
      .action-card--failed & {
        color: var(--red-800);
      }
    }

    &__expires {
      font-size: 0.6875rem;
      color: var(--slate-500);
      font-variant-numeric: tabular-nums;
    }

    &__summary {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-900);
      line-height: 1.4;
    }

    &__preview {
      list-style: none;
      margin: 0.25rem 0 0;
      padding: 0.5rem 0.625rem;
      background: rgba(255, 255, 255, 0.6);
      border-radius: 0.375rem;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      font-size: 0.8125rem;

      li {
        display: flex;
        gap: 0.375rem;
        line-height: 1.4;
      }
    }

    &__preview-key {
      color: var(--slate-500);
      flex-shrink: 0;
      min-width: 90px;
    }

    &__preview-value {
      color: var(--slate-800);
      font-weight: 500;
      word-break: break-word;
    }

    &__actions {
      display: flex;
      gap: 0.375rem;
      margin-top: 0.25rem;
    }

    &__result {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.25rem 0.5rem;
      border-radius: 0.375rem;
      background: var(--green-100);
      color: var(--green-800);
      font-size: 0.75rem;
      font-weight: 500;
      align-self: flex-start;
      margin-top: 0.25rem;

      &--warning {
        background: var(--amber-100);
        color: var(--amber-800);
      }

      &--muted {
        background: var(--slate-100);
        color: var(--slate-600);
      }

      &--error {
        background: var(--red-100);
        color: var(--red-800);
      }
    }
  }
</style>
